import React, { useState } from 'react';
import { X, Mail, ShieldCheck, Sparkles, Send, CheckCircle2, AlertCircle } from 'lucide-react';
import { invitationService } from '../lib/invitationService';
import { IntendedUsage } from '../types/invitation';

interface InvitationRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  lang?: string;
}

export const InvitationRequestModal: React.FC<InvitationRequestModalProps> = ({
  isOpen,
  onClose,
  lang = 'en',
}) => {
  const isHi = lang === 'hi';

  const [step, setStep] = useState<'details' | 'otp' | 'success'>('details');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [intendedUsage, setIntendedUsage] = useState<IntendedUsage>('seeker');
  const [reasonForJoining, setReasonForJoining] = useState('');
  const [referralCode, setReferralCode] = useState('');

  // Step 2 state
  const [requestId, setRequestId] = useState('');
  const [otp, setOtp] = useState('');
  const [remainingAttempts, setRemainingAttempts] = useState<number | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Status & loading
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmitDetails = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const cleanEmail = email.trim().toLowerCase();
    if (!fullName.trim()) {
      setError(isHi ? 'कृपया अपना पूरा नाम दर्ज करें' : 'Please enter your full name');
      return;
    }
    if (!cleanEmail || !cleanEmail.includes('@')) {
      setError(isHi ? 'कृपया एक मान्य ईमेल दर्ज करें' : 'Please enter a valid email address');
      return;
    }

    setLoading(true);
    const res = await invitationService.submitRequest({
      fullName: fullName.trim(),
      email: cleanEmail,
      intendedUsage,
      reasonForJoining: reasonForJoining.trim() || undefined,
      referralCode: referralCode.trim() || undefined,
    });
    setLoading(false);

    if (!res.success) {
      setError(res.error || (isHi ? 'अनुरोध विफल हुआ' : 'Failed to submit request'));
      return;
    }

    setRequestId(res.requestId || '');
    setStep('otp');
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!otp || otp.trim().length !== 6) {
      setError(isHi ? 'कृपया 6-अंकीय सत्यापन कोड दर्ज करें' : 'Please enter the 6-digit verification code');
      return;
    }

    setLoading(true);
    const res = await invitationService.verifyEmailOtp({
      requestId,
      email: email.trim().toLowerCase(),
      otp: otp.trim(),
    });
    setLoading(false);

    if (!res.success) {
      setError(res.error || (isHi ? 'सत्यापन विफल हुआ' : 'Verification failed'));
      if (res.remainingAttempts !== undefined) {
        setRemainingAttempts(res.remainingAttempts);
      }
      return;
    }

    setStep('success');
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0 || loading) return;
    setError(null);
    setLoading(true);

    const res = await invitationService.submitRequest({
      fullName: fullName.trim(),
      email: email.trim().toLowerCase(),
      intendedUsage,
      reasonForJoining: reasonForJoining.trim() || undefined,
      referralCode: referralCode.trim() || undefined,
    });
    setLoading(false);

    if (res.success) {
      setResendCooldown(60);
      const timer = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      setError(res.error || (isHi ? 'पुनः कोड भेजने में असमर्थ' : 'Could not resend code'));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in">
      <div className="relative w-full max-w-lg bg-slate-900 border border-amber-500/30 rounded-2xl shadow-2xl overflow-hidden p-6 text-slate-100">
        {/* Header */}
        <div className="flex items-center justify-between pb-4 border-b border-slate-800">
          <div className="flex items-center space-x-3">
            <div className="p-2 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-400">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-lg font-serif font-bold text-amber-300">
                {isHi ? 'वेदांग ज्योतिष आमंत्रण अनुरोध' : 'Request Circle Invitation'}
              </h3>
              <p className="text-xs text-slate-400">
                {isHi ? 'सीमित एवं प्रामाणिक वैदिक अनुसंधान समूह' : 'Curated Vedic Astrological Circle'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-slate-400 hover:text-slate-100 hover:bg-slate-800 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content per Step */}
        <div className="mt-4">
          {error && (
            <div className="mb-4 p-3 bg-red-950/60 border border-red-800/80 rounded-xl flex items-start space-x-2 text-red-300 text-sm">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {step === 'details' && (
            <form onSubmit={handleSubmitDetails} className="space-y-4">
              <p className="text-xs text-slate-300 leading-relaxed">
                {isHi
                  ? 'वेदांग ज्योतिष में नवीन खातों का सृजन केवल आमंत्रण द्वारा नियंत्रित है। अतिथि के रूप में सभी गणनाएँ पूर्णतः निःशुल्क खुली हैं।'
                  : 'Account registration is currently curated by invitation to preserve classical computational integrity. Guests can explore all astrological tools freely without an account.'}
              </p>

              <div>
                <label className="block text-xs font-medium text-slate-300 mb-1">
                  {isHi ? 'पूरा नाम' : 'Full Name'} *
                </label>
                <input
                  type="text"
                  required
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder={isHi ? 'उदा. आचार्य देवदत्त शर्मा' : 'e.g. Pt. Devadatta Sharma'}
                  className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-slate-100 text-sm focus:outline-none focus:border-amber-400"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-300 mb-1">
                  {isHi ? 'ईमेल पता' : 'Email Address'} *
                </label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@example.com"
                  className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-slate-100 text-sm focus:outline-none focus:border-amber-400"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-300 mb-1">
                  {isHi ? 'उद्देश्य' : 'Intended Usage'}
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setIntendedUsage('seeker')}
                    className={`py-2 px-3 rounded-lg text-xs font-medium border text-center transition ${
                      intendedUsage === 'seeker'
                        ? 'bg-amber-500/20 border-amber-400 text-amber-200'
                        : 'bg-slate-950 border-slate-800 text-slate-400 hover:border-slate-700'
                    }`}
                  >
                    {isHi ? 'जिज्ञासु / साधक' : 'Seeker / Personal'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setIntendedUsage('astrologer')}
                    className={`py-2 px-3 rounded-lg text-xs font-medium border text-center transition ${
                      intendedUsage === 'astrologer'
                        ? 'bg-amber-500/20 border-amber-400 text-amber-200'
                        : 'bg-slate-950 border-slate-800 text-slate-400 hover:border-slate-700'
                    }`}
                  >
                    {isHi ? 'ज्योतिषी / शोधार्थी' : 'Astrologer / Scholar'}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-300 mb-1">
                  {isHi ? 'जुड़ने का कारण (वैकल्पिक)' : 'Statement / Astrological Interest (Optional)'}
                </label>
                <textarea
                  value={reasonForJoining}
                  onChange={(e) => setReasonForJoining(e.target.value.slice(0, 500))}
                  rows={2}
                  placeholder={isHi ? 'अपनी ज्योतिषीय पृष्ठभूमि संक्षेप में साझा करें...' : 'Briefly describe your astrological focus or background...'}
                  className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-slate-100 text-sm focus:outline-none focus:border-amber-400"
                />
                <span className="text-[10px] text-slate-500">{reasonForJoining.length}/500</span>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-300 mb-1">
                  {isHi ? 'रेफरल कोड (यदि कोई हो)' : 'Referral Code (Optional)'}
                </label>
                <input
                  type="text"
                  value={referralCode}
                  onChange={(e) => setReferralCode(e.target.value)}
                  placeholder="e.g. VEDANGA-108"
                  className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg text-slate-100 text-sm focus:outline-none focus:border-amber-400"
                />
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-2.5 px-4 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-slate-950 font-semibold rounded-xl text-sm transition shadow-lg shadow-amber-900/30 flex items-center justify-center space-x-2 disabled:opacity-50"
                >
                  {loading ? (
                    <span>{isHi ? 'प्रक्रिया चल रही है...' : 'Sending Code...'}</span>
                  ) : (
                    <>
                      <span>{isHi ? 'सत्यापन कोड भेजें' : 'Send Verification Code'}</span>
                      <Send className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          {step === 'otp' && (
            <form onSubmit={handleVerifyOtp} className="space-y-4">
              <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl text-xs text-amber-200">
                <p>
                  {isHi
                    ? `हमने ${email} पर 6-अंकीय सत्यापन कोड भेजा है।`
                    : `We sent a 6-digit verification code to ${email}.`}
                </p>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-300 mb-1">
                  {isHi ? '6-अंकीय सत्यापन कोड' : '6-Digit Verification Code'}
                </label>
                <input
                  type="text"
                  maxLength={6}
                  required
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  placeholder="123456"
                  className="w-full px-3 py-2.5 tracking-widest text-center font-mono text-lg bg-slate-950 border border-slate-700 rounded-lg text-amber-400 focus:outline-none focus:border-amber-400"
                />
                {remainingAttempts !== null && (
                  <p className="text-[11px] text-amber-400 mt-1 text-center">
                    {isHi
                      ? `शेष प्रयास: ${remainingAttempts}`
                      : `Attempts remaining: ${remainingAttempts}`}
                  </p>
                )}
              </div>

              <div className="flex items-center justify-between text-xs text-slate-400">
                <button
                  type="button"
                  onClick={() => setStep('details')}
                  className="hover:text-slate-200 underline"
                >
                  {isHi ? 'ईमेल बदलें' : 'Change email'}
                </button>
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={resendCooldown > 0 || loading}
                  className="hover:text-amber-300 disabled:opacity-50"
                >
                  {resendCooldown > 0
                    ? `${isHi ? 'पुनः भेजें' : 'Resend in'} ${resendCooldown}s`
                    : isHi ? 'कोड पुनः भेजें' : 'Resend Code'}
                </button>
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-2.5 px-4 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-slate-950 font-semibold rounded-xl text-sm transition shadow-lg shadow-amber-900/30 flex items-center justify-center space-x-2 disabled:opacity-50"
                >
                  {loading ? (
                    <span>{isHi ? 'सत्यापन हो रहा है...' : 'Verifying...'}</span>
                  ) : (
                    <>
                      <span>{isHi ? 'ईमेल सत्यापित करें' : 'Verify & Submit Application'}</span>
                      <ShieldCheck className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          {step === 'success' && (
            <div className="py-6 text-center space-y-4">
              <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
                <CheckCircle2 className="w-8 h-8" />
              </div>
              <h4 className="text-lg font-serif font-bold text-slate-100">
                {isHi ? 'अनुरोध सफलतापूर्वक दर्ज' : 'Invitation Request Enqueued'}
              </h4>
              <p className="text-xs text-slate-300 max-w-sm mx-auto leading-relaxed">
                {isHi
                  ? `आपका ईमेल ${email} सत्यापित हो चुका है। प्रशासनिक स्वीकृति के उपरांत आपको आमंत्रण लिंक ईमेल द्वारा प्रेषित किया जाएगा।`
                  : `Your email (${email}) has been verified. Our circle administrators will review your request. You will receive an invitation link via email once approved.`}
              </p>

              <div className="pt-4">
                <button
                  onClick={onClose}
                  className="w-full py-2.5 px-4 bg-slate-800 hover:bg-slate-700 text-slate-200 font-medium rounded-xl text-sm transition"
                >
                  {isHi ? 'अतिथि के रूप में जारी रखें' : 'Continue Exploring as Guest'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

